// Per-kind payload renderer. The protocol envelope's payload_json shape
// varies by TxKind (SPEC.md §2 + per-kind addenda); we render a friendly
// summary for every known kind and fall back to a JSON dump for unknowns.
//
// Keep this dependency-light — no schema validators; treat fields defensively.

import { kindLabel, microsToCredits, type Tx } from "../api";

function tryParse(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function row(label: string, value: React.ReactNode): React.ReactElement {
  return (
    <div key={label} className="flex justify-between gap-3 py-1 border-b border-mesh-panel/40 last:border-0">
      <span className="text-xs uppercase text-mesh-mute">{label}</span>
      <span className="text-sm break-all text-right">{value}</span>
    </div>
  );
}

function rawJson(payload: string): React.ReactElement {
  return (
    <pre className="text-xs overflow-auto bg-mesh-bg p-3 rounded">
      {(() => {
        try {
          return JSON.stringify(JSON.parse(payload), null, 2);
        } catch {
          return payload;
        }
      })()}
    </pre>
  );
}

export function formatPayload(tx: Tx): React.ReactElement {
  const parsed = tryParse(tx.payload_json) as Record<string, unknown> | null;
  switch (tx.kind) {
    case 0: // Transfer
      // Transfer has no extra payload — amount lives on the envelope itself.
      return (
        <div className="text-sm text-mesh-mute">
          {tx.amount === "0"
            ? "no amount (zero-value transfer)"
            : `transfers ${microsToCredits(tx.amount)} cr — see envelope fields above`}
        </div>
      );
    case 1: // Witness
      return (
        <div className="space-y-1">
          {row(
            "kind",
            <span className="text-mesh-accent">witness envelope</span>,
          )}
          <div className="text-xs text-mesh-mute pt-2">
            full witness metadata (round, vrf, included tx ids) lives on the
            companion witnesses row — open the linked round.
          </div>
        </div>
      );
    case 2: // Freeze
      return (
        <div className="space-y-1">
          {row(
            "freeze target",
            <span className="hex">{(parsed?.["0"] as string) ?? "—"}</span>,
          )}
          {row("reason_code", String(parsed?.["1"] ?? "—"))}
        </div>
      );
    case 3: // Demurrage
      return (
        <div className="space-y-1">
          {row("rate_micros", String(parsed?.["0"] ?? "—"))}
          {row("applied_at_round", String(parsed?.["1"] ?? "—"))}
        </div>
      );
    case 4: // Snapshot
      return (
        <div className="space-y-1">
          {row(
            "balances_root",
            <span className="hex">{(parsed?.["0"] as string) ?? "—"}</span>,
          )}
          {row("at_witness_round", String(parsed?.["1"] ?? "—"))}
        </div>
      );
    case 5: // Recovery
      return (
        <div className="space-y-1">
          {row(
            "from_account",
            <span className="hex">{(parsed?.["0"] as string) ?? "—"}</span>,
          )}
          {row(
            "to_account",
            <span className="hex">{(parsed?.["1"] as string) ?? "—"}</span>,
          )}
        </div>
      );
    case 6: // Join
      return (
        <div className="space-y-1">
          {row(
            "account_id",
            <span className="hex">{(parsed?.["0"] as string) ?? "—"}</span>,
          )}
          {row(
            "credit_limit",
            parsed?.["1"]
              ? `${microsToCredits(String(parsed["1"]))} cr`
              : "—",
          )}
        </div>
      );
    case 7: // Bond
      return (
        <div className="space-y-1">
          {row(
            "bond_amount",
            parsed?.["0"]
              ? `${microsToCredits(String(parsed["0"]))} cr`
              : "—",
          )}
          {row("lock_rounds", String(parsed?.["1"] ?? "—"))}
        </div>
      );
    case 8: // Unbond
      return (
        <div className="space-y-1">
          {row("bond_tx_id", <span className="hex">{(parsed?.["0"] as string) ?? "—"}</span>)}
        </div>
      );
    case 9: // SetCreditLimit
      return (
        <div className="space-y-1">
          {row(
            "new_limit",
            parsed?.["0"]
              ? `${microsToCredits(String(parsed["0"]))} cr`
              : "—",
          )}
        </div>
      );
    case 10: // GovernorRotation
      return (
        <div className="space-y-1">
          {row("rotation_epoch", String(parsed?.["0"] ?? "—"))}
          {row(
            "incoming_governors",
            Array.isArray(parsed?.["1"]) ? `${(parsed["1"] as unknown[]).length} pks` : "—",
          )}
          {row("threshold", String(parsed?.["2"] ?? "—"))}
        </div>
      );
    default:
      return (
        <div className="space-y-2">
          <div className="text-xs text-mesh-warn">
            unknown kind {tx.kind} ({kindLabel(tx.kind)}) — showing raw payload
          </div>
          {rawJson(tx.payload_json)}
        </div>
      );
  }
}
